import { Dialog, Flex, Text, Box, Spinner } from "@radix-ui/themes";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  fetchProofsForStoreActivity,
  type AuditProof,
} from "../../../state/audit";

interface ProofModalProps {
  open: boolean;
  onClose: () => void;
  storeActivityId: number;
  storeId: number;
  storeName: string;
  storeReference: string;
  periodStart: string;
  periodEnd: string;
}

export function ProofModal({
  open,
  onClose,
  storeActivityId,
  storeId,
  storeName,
  storeReference,
  periodStart,
  periodEnd,
}: ProofModalProps) {
  const { t } = useTranslation();
  const [proofs, setProofs] = useState<AuditProof[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    setLoading(true);
    setError(null);

    fetchProofsForStoreActivity(
      storeActivityId,
      storeId,
      periodStart,
      periodEnd,
    )
      .then((data) => {
        setProofs(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || t("audit.failedLoadProofs"));
        setLoading(false);
      });
  }, [open, storeActivityId, storeId, periodStart, periodEnd]);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getProofLabel = (proof: AuditProof) => {
    if (proof.target === "store") {
      return t("audit.storeFront");
    }
    return proof.description;
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Content maxWidth="900px">
        <Flex justify="between" align="center" mb="4">
          <Dialog.Title>
            {storeName} ({storeReference})
          </Dialog.Title>
          <Dialog.Close>
            <button
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              <Cross2Icon width={20} height={20} />
            </button>
          </Dialog.Close>
        </Flex>

        {loading && (
          <Flex justify="center" align="center" py="8">
            <Spinner size="3" />
          </Flex>
        )}

        {error && (
          <Text color="red" size="2">
            {error}
          </Text>
        )}

        {!loading && !error && proofs.length === 0 && (
          <Text color="gray" size="2">
            {t("audit.noProofsFound")}
          </Text>
        )}

        {!loading && !error && proofs.length > 0 && (
          <Flex gap="4" wrap="wrap" justify="center">
            {proofs.map((proof, index) => (
              <Box
                key={index}
                style={{
                  border: "1px solid var(--gray-6)",
                  borderRadius: "8px",
                  overflow: "hidden",
                  width: "280px",
                }}
              >
                <Box
                  style={{
                    width: "100%",
                    height: "200px",
                    backgroundColor: "var(--gray-3)",
                  }}
                >
                  <img
                    src={proof.imageUrl}
                    alt={getProofLabel(proof)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <Box p="3">
                  <Text weight="bold" size="2" as="div">
                    {getProofLabel(proof)}
                  </Text>
                  <Text color="gray" size="1" as="div">
                    {formatTimestamp(proof.timestamp)}
                  </Text>
                </Box>
              </Box>
            ))}
          </Flex>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
